import { supabase } from '../supabase';
import type { MessageAttachment, MessageAttachmentType } from '../stores/teamChatStore';

const BUCKET = 'team-chat-files';
const SIGNED_URL_DURATION = 86400; // 24 ore

function getAttachmentType(mimeType: string): MessageAttachmentType {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'file';
}

export async function uploadChatFile(
  file: File,
  chatId: string,
  onProgress?: (pct: number) => void,
): Promise<MessageAttachment> {
  const uuid = crypto.randomUUID();
  const storagePath = `${chatId}/${uuid}-${file.name}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  // Supabase JS v2 upload doesn't support progress callbacks,
  // so we signal completion after the upload finishes.
  onProgress?.(100);

  // Get a signed URL (valid 1 hour) — works for both public and private buckets
  const { data: urlData, error: urlError } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_DURATION);

  if (urlError || !urlData?.signedUrl) {
    throw urlError ?? new Error('Impossibile ottenere URL del file');
  }

  const attachment: MessageAttachment = {
    type: getAttachmentType(file.type),
    url: urlData.signedUrl,
    storage_path: storagePath,
    storage_bucket: BUCKET,
    name: file.name,
    size: file.size,
    mime_type: file.type,
  };

  return attachment;
}

/**
 * Rigenera signed URL per un singolo attachment che ha storage_path.
 * Ritorna l'attachment con URL aggiornato, oppure invariato se manca storage_path.
 */
export async function refreshAttachmentUrl(att: MessageAttachment): Promise<MessageAttachment> {
  if (!att.storage_path) return att;
  const bucket = att.storage_bucket || BUCKET;
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(att.storage_path, SIGNED_URL_DURATION);
  if (error || !data?.signedUrl) return att;
  return { ...att, url: data.signedUrl };
}

/**
 * Rigenera signed URL per tutti gli attachment di un array di messaggi.
 * Batch-friendly: usa createSignedUrls per minimizzare le chiamate.
 */
export async function refreshMessageAttachmentUrls<T extends { attachments?: MessageAttachment[] }>(
  messages: T[]
): Promise<T[]> {
  // Raccogli tutti i path che hanno bisogno di refresh
  const pathMap = new Map<string, { msgIdx: number; attIdx: number; bucket: string }>();
  const pathsByBucket = new Map<string, string[]>();

  messages.forEach((msg, msgIdx) => {
    (msg.attachments || []).forEach((att, attIdx) => {
      if (att.storage_path) {
        const bucket = att.storage_bucket || BUCKET;
        const key = `${bucket}::${att.storage_path}`;
        pathMap.set(key, { msgIdx, attIdx, bucket });
        if (!pathsByBucket.has(bucket)) pathsByBucket.set(bucket, []);
        pathsByBucket.get(bucket)!.push(att.storage_path);
      }
    });
  });

  if (pathMap.size === 0) return messages;

  // Batch signed URL per bucket
  const urlMap = new Map<string, string>();
  for (const [bucket, paths] of pathsByBucket) {
    const { data } = await supabase.storage
      .from(bucket)
      .createSignedUrls(paths, SIGNED_URL_DURATION);
    if (data) {
      data.forEach(item => {
        if (item.signedUrl && item.path) {
          urlMap.set(`${bucket}::${item.path}`, item.signedUrl);
        }
      });
    }
  }

  // Applica gli URL aggiornati
  const result = messages.map(msg => ({ ...msg }));
  for (const [key, { msgIdx, attIdx }] of pathMap) {
    const newUrl = urlMap.get(key);
    if (newUrl && result[msgIdx].attachments) {
      result[msgIdx] = {
        ...result[msgIdx],
        attachments: result[msgIdx].attachments!.map((att, i) =>
          i === attIdx ? { ...att, url: newUrl } : att
        ),
      };
    }
  }

  return result;
}
